import { useEffect, useId, useRef, useState } from 'react';
import { PiCaretDownLight } from 'react-icons/pi';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { primaryNav, servicePages } from '../../data/navigation';
import { Wordmark } from '../Art';
import { ButtonLink } from '../ui/ButtonLink';
import styles from './Header.module.css';

// NavLink matches "/#x" as "/", so hash links would look active on the home page
const NavItem = ({ to, ...rest }) =>
  to.includes('#') ? <Link to={to} {...rest} /> : <NavLink to={to} end={to === '/'} {...rest} />;

export function Header() {
  const { pathname, hash } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [practiceOpen, setPracticeOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const sentinelRef = useRef(null);
  const practiceId = useId();
  const menuId = useId();

  // Close menus on navigation
  const [lastLocation, setLastLocation] = useState(pathname + hash);
  if (lastLocation !== pathname + hash) {
    setLastLocation(pathname + hash);
    setMenuOpen(false);
    setPracticeOpen(false);
  }

  // Sentinel at page top toggles the solid header without a scroll listener
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => setScrolled(!entry.isIntersecting));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    if (!menuOpen) return;
    const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const practiceActive = pathname.startsWith('/services');

  return (
    <>
      <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />
      <header className={styles.header} data-scrolled={scrolled} data-open={menuOpen}>
        <div className={styles.bar}>
          <Link to="/" className={styles.wordmark} aria-label="Shumunov Solutions, home">
            <Wordmark className={styles.full} />
            <Wordmark compact className={styles.short} />
          </Link>

          <nav className={styles.desktopNav} aria-label="Primary">
            <div
              className={styles.dropdown}
              onMouseEnter={() => setPracticeOpen(true)}
              onMouseLeave={() => setPracticeOpen(false)}
              onBlur={(e) => !e.currentTarget.contains(e.relatedTarget) && setPracticeOpen(false)}
            >
              <button
                type="button"
                className={styles.navItem}
                aria-expanded={practiceOpen}
                aria-controls={practiceId}
                aria-current={practiceActive ? 'page' : undefined}
                onClick={() => setPracticeOpen((open) => !open)}
                onKeyDown={(e) => e.key === 'Escape' && setPracticeOpen(false)}
              >
                Practice <PiCaretDownLight aria-hidden="true" />
              </button>
              <div id={practiceId} className={styles.panel} data-open={practiceOpen}>
                <NavLink to="/services" end className={styles.panelLead}>
                  All services
                </NavLink>
                <ul>
                  {servicePages.map(({ to, label }) => (
                    <li key={to}>
                      <NavLink to={to}>{label}</NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {primaryNav.map(({ to, label }) => (
              <NavItem key={to} to={to} className={styles.navItem}>
                {label}
              </NavItem>
            ))}
          </nav>

          <ButtonLink to="/contact" className={styles.cta}>
            Begin a project
          </ButtonLink>

          <button
            type="button"
            className={styles.burger}
            aria-expanded={menuOpen}
            aria-controls={menuId}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
          </button>
        </div>

        <div id={menuId} className={styles.overlay} inert={menuOpen ? undefined : ''}>
          <nav aria-label="Mobile">
            <ol className={styles.overlayList}>
              {[{ to: '/', label: 'Home' }, { to: '/services', label: 'Practice' }, ...primaryNav].map(
                ({ to, label }, i) => (
                  <li key={to} style={{ '--i': i }}>
                    <NavItem to={to}>{label}</NavItem>
                  </li>
                )
              )}
            </ol>
            <ul className={styles.overlayServices}>
              {servicePages.map(({ to, label }) => (
                <li key={to}>
                  <NavLink to={to}>{label}</NavLink>
                </li>
              ))}
            </ul>
            <ButtonLink to="/contact">Begin a project</ButtonLink>
          </nav>
        </div>
      </header>
    </>
  );
}
